import { t } from "i18next";
import {
  BookOpen,
  ClipboardList,
  CreditCard,
  FileAudio,
  Link2,
  Puzzle,
  ShieldCheck,
  UserCog,
  UserRound,
} from "lucide-react";
import type { JSX } from "react";
import { useEffect, useMemo } from "react";
import { AppConfig } from "@/common/appConfig";
import { Layout } from "@/components/layout";
import AccountSettings from "@/pages/Settings/AccountSettings/AccountSettings";
import { useAuthStore } from "@/store/auth.store";
import HashTabs, { type HashTab } from "../../components/ui/HashTabs";
import { ChromeExtension } from "./ChromeExtension";
import SubscriptionTopBar from "./components/SubscriptionTopBar";
import { EmrLaunchSettings } from "./EmrLaunchSettings";
import { PreChartSettings } from "./PreChartSettings";
import { ProfileSettings } from "./ProfileSettings";
import { PlanSummary } from "./redesign/PlanSummary";
import { SettingsHome } from "./redesign/SettingsHome";
import { SubscriptionPanel } from "./redesign/SubscriptionPanel";
import { SecuritySettings } from "./SecuritySettings";
import SubscriptionSettings from "./SubscriptionSettings";
import TranscriptionSettings from "./TranscriptionSettings";
import { VocabularySettings } from "./VocabularySettings";

export const Settings = (): JSX.Element => {
  const { userData, getUser, loading } = useAuthStore();

  const tabs: (HashTab & {
    icon: JSX.Element;
    description: string;
  })[] = useMemo(
    () => [
      {
        label: t("settings.profile.menuItem"),
        value: "profile",
        icon: <UserRound aria-hidden />,
        description: t("settings.home.descriptions.profile"),
        content: <ProfileSettings />,
      },
      {
        label: t("settings.transcription.menuItem"),
        value: "transcription",
        icon: <FileAudio aria-hidden />,
        description: t("settings.home.descriptions.transcription"),
        content: <TranscriptionSettings />,
      },
      ...(AppConfig.enableSavedVocabulary
        ? [
            {
              label: t("settings.vocabulary.menuItem"),
              value: "vocabulary",
              icon: <BookOpen aria-hidden />,
              description: t("settings.vocabulary.subtitle"),
              content: <VocabularySettings />,
            },
          ]
        : []),
      ...(AppConfig.enablePreChart
        ? [
            {
              label: t("preChart.settings.menuItem"),
              value: "pre-chart",
              icon: <ClipboardList aria-hidden />,
              description: t("preChart.settings.description"),
              content: <PreChartSettings />,
            },
          ]
        : []),
      {
        label: t("settings.chromeExtension.menuItem"),
        value: "chrome-extension",
        icon: <Puzzle aria-hidden />,
        description: t("settings.home.descriptions.chromeExtension"),
        content: <ChromeExtension />,
      },
      {
        label: t("settings.emrLaunch.menuItem"),
        value: "emr-launch",
        icon: <Link2 aria-hidden />,
        description: t("settings.home.descriptions.emrLaunch"),
        content: <EmrLaunchSettings />,
      },
      {
        label: t("settings.subscription.menuItem"),
        value: "subscription",
        icon: <CreditCard aria-hidden />,
        description: t("settings.home.descriptions.subscription"),
        content: AppConfig.enableAppRedesign ? (
          <SubscriptionPanel />
        ) : (
          <SubscriptionSettings />
        ),
      },
      {
        label: t("settings.security.menuItem"),
        value: "security",
        icon: <ShieldCheck aria-hidden />,
        description: t("settings.home.descriptions.security"),
        content: <SecuritySettings />,
      },
      {
        label: t("settings.account.menuItem"),
        value: "account",
        icon: <UserCog aria-hidden />,
        description: t("settings.home.descriptions.account"),
        content: <AccountSettings />,
      },
    ],
    [],
  );

  useEffect(() => {
    if (userData) {
      getUser();
    }
  }, []);

  if (AppConfig.enableAppRedesign)
    return (
      <Layout
        isLoading={loading}
        navbarTitle={t("settings.title")}
        breadcrumbs={[
          { name: t("settings.title"), href: `/settings`, current: true },
        ]}
      >
        <SettingsHome
          sections={tabs}
          summary={userData?.planName ? <PlanSummary /> : undefined}
        />
      </Layout>
    );

  return (
    <Layout
      isLoading={loading}
      navbarTitle={t("settings.title")}
      breadcrumbs={[
        { name: t("settings.title"), href: `/settings`, current: true },
      ]}
    >
      <SubscriptionTopBar shouldShowExpirationDate={!!userData?.planName} />
      <div className="mx-auto max-w-5xl">
        <HashTabs tabs={tabs} />
      </div>
    </Layout>
  );
};
